import { useEffect, useState } from "react";
import { Database, Route, ShieldCheck, Users } from "lucide-react";
import { supabase } from "../../lib/supabase";
import {
  getActiveDerivedLayerRelease,
  probeActiveDerivedLayerAvailability,
  THERA_DERIVED_LAYERS_ENABLED,
  THERA_DERIVED_LAYER_RENDERING_ENABLED,
  type ActiveDerivedLayerRelease,
  type DerivedLayerAvailabilityProbe,
} from "../../lib/derived-layer-release";

type SurveySummary = {
  total: number;
  pending: number;
  approved: number;
  contributors: number;
};

type SurveyActivity = {
  distance_m: number | null;
  duration_seconds: number | null;
  points_count: number | null;
  source_platform: string | null;
  track_geojson: unknown | null;
};

type SurveySession = {
  id: string;
  activity_id: string | null;
  source_kind: string;
  review_status: string;
  use_scopes: string[];
  created_at: string;
  activity: SurveyActivity | null;
};

type AuditRow = {
  action?: string | null;
  entity_id: string | null;
  metadata: unknown;
};

type EngineSource = {
  contributionId: string;
  session: SurveySession;
  regionId: string;
  buildId: string | null;
  candidateId: string | null;
  candidateDecision: "approved" | "rejected" | null;
  releaseId: string | null;
  releaseActive: boolean;
};

type DerivedLayerDetectionStatus =
  | "disabled"
  | "loading"
  | "detected"
  | "none"
  | "error";

type DerivedLayerR2ProbeStatus =
  | "idle"
  | "loading"
  | "reachable"
  | "mismatch"
  | "error";

const ENGINE_ALGORITHM_VERSION = "field-pass-through-v1";
const PILOT_RELEASE_CANDIDATE_ID = "c139ef12-9909-44f1-a0de-d49103647b3b";
const PILOT_RELEASE_ID = "9de19f75-53d9-4681-b413-0b785437233f";
const PILOT_RELEASE_FILE_PATH =
  "derived/terrain-engine/sierra-sur/field/v1/sierra-sur-field-v1.pmtiles";
const PILOT_RELEASE_SIZE_BYTES = 19704;
const PILOT_RELEASE_SHA256 =
  "adbfbcf520fbb208a703a628dc43123934e1b8fd1381195eebcba77a8606cb92";

const REGION_OPTIONS = [
  ["sierra-sur", "Sierra Sur · Loja / Azuay / Cañar"],
  ["sierra-centro-oeste", "Sierra Centro Oeste · Bolívar / Chimborazo"],
  ["sierra-centro-este", "Sierra Centro Este · Cotopaxi / Tungurahua"],
  ["sierra-norte", "Sierra Norte"],
  ["costa-sur", "Costa Sur"],
  ["costa-centro", "Costa Centro"],
  ["costa-norte", "Costa Norte"],
  ["amazonia-sur", "Amazonía Sur"],
  ["amazonia-centro", "Amazonía Centro"],
  ["amazonia-norte", "Amazonía Norte"],
  ["galapagos", "Galápagos"],
] as const;

function numberValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatDistance(value: number | null | undefined) {
  const metres = numberValue(value);
  if (metres >= 1000) return `${(metres / 1000).toFixed(2)} km`;
  return `${Math.round(metres)} m`;
}

function formatDuration(value: number | null | undefined) {
  const seconds = Math.max(0, Math.round(numberValue(value)));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

function metadataObject(metadata: unknown) {
  return metadata && typeof metadata === "object"
    ? (metadata as Record<string, unknown>)
    : null;
}

function metadataString(metadata: unknown, key: string) {
  const raw = metadataObject(metadata)?.[key];
  return typeof raw === "string" ? raw : null;
}

function metadataSessionId(metadata: unknown) {
  return metadataString(metadata, "session_id");
}

export function FieldSurveyContributionAdminPanel() {
  const [summary, setSummary] = useState<SurveySummary | null>(null);
  const [pending, setPending] = useState<SurveySession[]>([]);
  const [approvedReady, setApprovedReady] = useState<SurveySession[]>([]);
  const [engineSources, setEngineSources] = useState<EngineSource[]>([]);
  const [regionBySession, setRegionBySession] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [activeDerivedRelease, setActiveDerivedRelease] =
    useState<ActiveDerivedLayerRelease | null>(null);
  const [derivedDetectionStatus, setDerivedDetectionStatus] =
    useState<DerivedLayerDetectionStatus>(
      THERA_DERIVED_LAYERS_ENABLED ? "loading" : "disabled"
    );
  const [derivedDetectionError, setDerivedDetectionError] =
    useState<string | null>(null);
  const [derivedR2Probe, setDerivedR2Probe] =
    useState<DerivedLayerAvailabilityProbe | null>(null);
  const [derivedR2ProbeStatus, setDerivedR2ProbeStatus] =
    useState<DerivedLayerR2ProbeStatus>("idle");
  const [derivedR2ProbeError, setDerivedR2ProbeError] =
    useState<string | null>(null);

  const loadPanel = async () => {
    const client = supabase;
    if (!client) return;

    setLoading(true);
    setMessage(null);

    const sessionSelect =
      "id,activity_id,source_kind,review_status,use_scopes,created_at,activity:activities!field_survey_sessions_activity_id_fkey(distance_m,duration_seconds,points_count,source_platform,track_geojson)";

    const [summaryResult, pendingResult, approvedResult, auditResult] =
      await Promise.all([
        client.rpc("admin_field_survey_summary"),
        client
          .from("field_survey_sessions")
          .select(sessionSelect)
          .eq("review_status", "pending")
          .order("created_at", { ascending: false }),
        client
          .from("field_survey_sessions")
          .select(sessionSelect)
          .eq("review_status", "approved")
          .order("created_at", { ascending: false }),
        client
          .from("audit_events")
          .select("action,entity_id,metadata")
          .in("action", [
            "validated_map_contribution_created",
            "terrain_engine_build_created",
            "terrain_engine_candidate_created",
            "terrain_engine_candidate_reviewed",
            "derived_layer_release_staged",
            "derived_layer_release_activated",
          ])
          .order("created_at", { ascending: false })
          .limit(500),
      ]);

    const errors = [
      summaryResult.error ? `Resumen: ${summaryResult.error.message}` : null,
      pendingResult.error ? `Pendientes: ${pendingResult.error.message}` : null,
      approvedResult.error ? `Aprobados: ${approvedResult.error.message}` : null,
      auditResult.error ? `Auditoría: ${auditResult.error.message}` : null,
    ].filter(Boolean);

    if (errors.length) setMessage(errors.join(" | "));

    if (
      !summaryResult.error &&
      summaryResult.data &&
      typeof summaryResult.data === "object"
    ) {
      const raw = summaryResult.data as Record<string, unknown>;
      setSummary({
        total: numberValue(raw.total),
        pending: numberValue(raw.pending),
        approved: numberValue(raw.approved),
        contributors: numberValue(raw.contributors),
      });
    }

    setPending(
      pendingResult.error
        ? []
        : ((pendingResult.data ?? []) as unknown as SurveySession[])
    );

    const approvedSessions = approvedResult.error
      ? []
      : ((approvedResult.data ?? []) as unknown as SurveySession[]);

    const auditRows = auditResult.error
      ? []
      : ((auditResult.data ?? []) as AuditRow[]);

    const contributionRows = auditRows.filter(
      (row) => row.action === "validated_map_contribution_created"
    );
    const buildRows = auditRows.filter(
      (row) => row.action === "terrain_engine_build_created"
    );
    const candidateRows = auditRows.filter(
      (row) => row.action === "terrain_engine_candidate_created"
    );
    const candidateReviewRows = auditRows.filter(
      (row) => row.action === "terrain_engine_candidate_reviewed"
    );
    const releaseRows = auditRows.filter(
      (row) => row.action === "derived_layer_release_staged"
    );
    const activatedReleaseRows = auditRows.filter(
      (row) => row.action === "derived_layer_release_activated"
    );

    const converted = new Set<string>();
    for (const row of contributionRows) {
      const sessionId = metadataSessionId(row.metadata);
      if (sessionId) converted.add(sessionId);
    }

    const ready = approvedSessions.filter((session) => !converted.has(session.id));
    setApprovedReady(ready);

    setRegionBySession((current) => {
      const next = { ...current };
      for (const session of ready) {
        if (!next[session.id]) next[session.id] = "sierra-sur";
      }
      return next;
    });

    const sources: EngineSource[] = [];
    for (const contribution of contributionRows) {
      const contributionId = contribution.entity_id;
      const sessionId = metadataSessionId(contribution.metadata);
      const regionId = metadataString(contribution.metadata, "region_id");
      const targetLayer = metadataString(contribution.metadata, "target_layer");

      if (!contributionId || !sessionId || !regionId || targetLayer !== "field") {
        continue;
      }

      const session = approvedSessions.find((item) => item.id === sessionId);
      if (!session) continue;

      const build = buildRows.find(
        (row) =>
          metadataString(row.metadata, "region_id") === regionId &&
          metadataString(row.metadata, "layer") === "field" &&
          metadataString(row.metadata, "algorithm_version") ===
            ENGINE_ALGORITHM_VERSION
      );

      const buildId = build?.entity_id ?? null;
      const candidate = buildId
        ? candidateRows.find(
            (row) => metadataString(row.metadata, "build_id") === buildId
          )
        : null;

      const candidateId = candidate?.entity_id ?? null;
      const review = candidateId
        ? candidateReviewRows.find((row) => row.entity_id === candidateId)
        : null;
      const decision = metadataString(review?.metadata, "decision");
      const release = candidateId
        ? releaseRows.find(
            (row) => metadataString(row.metadata, "candidate_id") === candidateId
          )
        : null;

      sources.push({
        contributionId,
        session,
        regionId,
        buildId,
        candidateId,
        candidateDecision:
          decision === "approved" || decision === "rejected" ? decision : null,
        releaseId: release?.entity_id ?? null,
        releaseActive: Boolean(
          release?.entity_id &&
            activatedReleaseRows.some((row) => row.entity_id === release.entity_id)
        ),
      });
    }

    setEngineSources(sources);
    setLoading(false);
  };

  const detectActiveDerivedRelease = async () => {
    if (!THERA_DERIVED_LAYERS_ENABLED) {
      setActiveDerivedRelease(null);
      setDerivedDetectionError(null);
      setDerivedDetectionStatus("disabled");
      return;
    }

    setDerivedDetectionStatus("loading");
    setDerivedDetectionError(null);
    setDerivedR2Probe(null);
    setDerivedR2ProbeStatus("idle");
    setDerivedR2ProbeError(null);

    try {
      const release = await getActiveDerivedLayerRelease("sierra-sur", "field");
      setActiveDerivedRelease(release);
      setDerivedDetectionStatus(release ? "detected" : "none");
    } catch (error) {
      setActiveDerivedRelease(null);
      setDerivedDetectionError(
        error instanceof Error ? error.message : "Error desconocido al resolver el release activo."
      );
      setDerivedDetectionStatus("error");
    }
  };


  const probeActiveDerivedReleaseR2 = async () => {
    if (!activeDerivedRelease) return;

    setDerivedR2ProbeStatus("loading");
    setDerivedR2ProbeError(null);

    try {
      const probe = await probeActiveDerivedLayerAvailability(activeDerivedRelease);
      setDerivedR2Probe(probe);
      setDerivedR2ProbeStatus(probe.size_matches ? "reachable" : "mismatch");
    } catch (error) {
      setDerivedR2Probe(null);
      setDerivedR2ProbeError(
        error instanceof Error ? error.message : "Error desconocido al verificar R2."
      );
      setDerivedR2ProbeStatus("error");
    }
  };

  useEffect(() => {
    void loadPanel();
    void detectActiveDerivedRelease();
  }, []);

  const reviewSession = async (
    session: SurveySession,
    decision: "approved" | "rejected"
  ) => {
    const client = supabase;
    if (!client || busyId) return;

    const points = numberValue(session.activity?.points_count);
    const distance = numberValue(session.activity?.distance_m);

    if (decision === "approved" && (points < 2 || distance <= 0)) {
      setMessage(
        "No se aprobará esta sesión: necesita al menos 2 puntos GPS y distancia mayor que 0."
      );
      return;
    }

    setBusyId(session.id);
    setMessage(null);

    const { error } = await client.rpc("admin_review_field_survey_session", {
      p_session_id: session.id,
      p_decision: decision,
      p_notes:
        decision === "approved"
          ? "Aprobado desde THERA Admin Field Survey."
          : "Rechazado desde THERA Admin Field Survey por evidencia GPS insuficiente o no válida.",
    });

    if (error) {
      setMessage(
        `No se pudo ${decision === "approved" ? "aprobar" : "rechazar"}: ${error.message}`
      );
      setBusyId(null);
      return;
    }

    setMessage(
      decision === "approved"
        ? "Sesión Field Survey aprobada."
        : "Sesión Field Survey rechazada."
    );
    setBusyId(null);
    await loadPanel();
  };

  const createValidatedContribution = async (session: SurveySession) => {
    const client = supabase;
    if (!client || busyId) return;

    const points = numberValue(session.activity?.points_count);
    const distance = numberValue(session.activity?.distance_m);
    const geometry = session.activity?.track_geojson;
    const regionId = regionBySession[session.id] ?? "sierra-sur";

    if (points < 2 || distance <= 0 || !geometry || typeof geometry !== "object") {
      setMessage(
        "No se puede crear el aporte validado: la sesión necesita track GPS válido, al menos 2 puntos y distancia positiva."
      );
      return;
    }

    setBusyId(session.id);
    setMessage(null);

    const { data, error } = await client.rpc(
      "admin_create_validated_map_contribution",
      {
        p_session_id: session.id,
        p_region_id: regionId,
        p_target_layer: "field",
        p_feature_kind: "recorded_track",
        p_geometry: geometry,
        p_observation_ids: [],
        p_media_ids: [],
      }
    );

    if (error) {
      setMessage(`No se pudo crear el aporte validado: ${error.message}`);
      setBusyId(null);
      return;
    }

    setMessage(
      `Aporte validado creado para ${regionId}${typeof data === "string" ? ` · ${data}` : ""}.`
    );
    setBusyId(null);
    await loadPanel();
  };

  const createTerrainBuild = async (source: EngineSource) => {
    const client = supabase;
    if (!client || busyId || source.buildId) return;

    const points = numberValue(source.session.activity?.points_count);
    const geometry = source.session.activity?.track_geojson;

    if (points < 2 || !geometry || typeof geometry !== "object") {
      setMessage("No se puede crear el build: el aporte no conserva una geometría field válida.");
      return;
    }

    setBusyId(`build:${source.contributionId}`);
    setMessage(null);

    const { data, error } = await client.rpc("admin_create_terrain_engine_build", {
      p_region_id: source.regionId,
      p_layer: "field",
      p_source_contribution_ids: [source.contributionId],
      p_algorithm_version: ENGINE_ALGORITHM_VERSION,
      p_parameters: {
        mode: "pass-through",
        sourceVisits: 1,
        sourcePoints: points,
        candidateGeometry: "validated-contribution-geometry",
        phase: "survey-engine-phase3c",
      },
    });

    if (error) {
      setMessage(`No se pudo crear el Terrain Engine build: ${error.message}`);
      setBusyId(null);
      return;
    }

    setMessage(
      `Terrain Engine build creado${typeof data === "string" ? ` · ${data}` : ""}.`
    );
    setBusyId(null);
    await loadPanel();
  };

  const createTerrainCandidate = async (source: EngineSource) => {
    const client = supabase;
    if (!client || busyId || !source.buildId || source.candidateId) return;

    const points = numberValue(source.session.activity?.points_count);
    const geometry = source.session.activity?.track_geojson;

    if (points < 2 || !geometry || typeof geometry !== "object") {
      setMessage("No se puede crear el candidate: falta geometría field válida.");
      return;
    }

    setBusyId(`candidate:${source.contributionId}`);
    setMessage(null);

    const { data, error } = await client.rpc(
      "admin_create_terrain_engine_candidate",
      {
        p_build_id: source.buildId,
        p_geometry: geometry,
        p_metrics: {
          sourceVisits: 1,
          sourcePoints: points,
          acceptedPoints: points,
          rejectedPoints: 0,
          meanAccuracyM: null,
          maxDeviationM: null,
        },
      }
    );

    if (error) {
      setMessage(`No se pudo crear el Terrain Engine candidate: ${error.message}`);
      setBusyId(null);
      return;
    }

    setMessage(
      `Terrain Engine candidate creado${typeof data === "string" ? ` · ${data}` : ""}.`
    );
    setBusyId(null);
    await loadPanel();
  };

  const reviewTerrainCandidate = async (
    source: EngineSource,
    decision: "approved" | "rejected"
  ) => {
    const client = supabase;
    if (!client || busyId || !source.candidateId || source.candidateDecision) return;

    setBusyId(`review:${source.candidateId}`);
    setMessage(null);

    const { error } = await client.rpc("admin_review_terrain_engine_candidate", {
      p_candidate_id: source.candidateId,
      p_decision: decision,
      p_notes:
        decision === "approved"
          ? "Baseline field pass-through revisado y aprobado desde THERA Admin."
          : "Baseline field pass-through rechazado desde THERA Admin.",
    });

    if (error) {
      setMessage(
        `No se pudo ${decision === "approved" ? "aprobar" : "rechazar"} el candidate: ${error.message}`
      );
      setBusyId(null);
      return;
    }

    setMessage(
      decision === "approved"
        ? "Terrain Engine candidate aprobado. Aún no se ha creado ningún release ni PMTiles."
        : "Terrain Engine candidate rechazado. No se ha publicado ningún dato."
    );
    setBusyId(null);
    await loadPanel();
  };

  const stageDerivedLayerRelease = async (source: EngineSource) => {
    const client = supabase;
    if (
      !client ||
      busyId ||
      !source.candidateId ||
      source.candidateDecision !== "approved" ||
      source.releaseId
    ) {
      return;
    }

    if (source.candidateId !== PILOT_RELEASE_CANDIDATE_ID) {
      setMessage(
        "Release bloqueado: este PHASE3E solo registra el PMTiles piloto ya verificado de Sierra Sur field V1."
      );
      return;
    }

    setBusyId(`release:${source.candidateId}`);
    setMessage(null);

    const { data, error } = await client.rpc(
      "admin_create_derived_layer_release",
      {
        p_candidate_id: source.candidateId,
        p_file_path: PILOT_RELEASE_FILE_PATH,
        p_size_bytes: PILOT_RELEASE_SIZE_BYTES,
        p_sha256: PILOT_RELEASE_SHA256,
      }
    );

    if (error) {
      setMessage(`No se pudo crear el release staged: ${error.message}`);
      setBusyId(null);
      return;
    }

    setMessage(
      `Release staged creado${typeof data === "string" ? ` · ${data}` : ""}. Aún NO está activo.`
    );
    setBusyId(null);
    await loadPanel();
  };

  const activateDerivedLayerRelease = async (source: EngineSource) => {
    const client = supabase;
    if (
      !client ||
      busyId ||
      !source.releaseId ||
      source.releaseActive ||
      source.candidateDecision !== "approved"
    ) {
      return;
    }

    if (
      source.candidateId !== PILOT_RELEASE_CANDIDATE_ID ||
      source.releaseId !== PILOT_RELEASE_ID
    ) {
      setMessage(
        "Activación bloqueada: este PHASE3F solo activa el release piloto verificado Sierra Sur field V1."
      );
      return;
    }

    const confirmed = window.confirm(
      "Activar release V1 de Sierra Sur field? Esto marcará esta capa derivada como activa, sin reemplazar los PMTiles base."
    );
    if (!confirmed) return;

    setBusyId(`activate:${source.releaseId}`);
    setMessage(null);

    const { error } = await client.rpc(
      "admin_activate_derived_layer_release",
      { p_release_id: source.releaseId }
    );

    if (error) {
      setMessage(`No se pudo activar el release V1: ${error.message}`);
      setBusyId(null);
      return;
    }

    setMessage(
      "Release V1 activado. La capa derivada Sierra Sur field queda marcada como activa; los PMTiles base no fueron reemplazados."
    );
    setBusyId(null);
    await loadPanel();
  };

  return (
    <>
      {summary && (
        <section className="admin-metrics" aria-label="Resumen de THERA Field Survey">
          <article><Database /><div><strong>{summary.total}</strong><span>Aportes Field Survey</span></div></article>
          <article><Route /><div><strong>{summary.pending}</strong><span>Pendientes de validar</span></div></article>
          <article><ShieldCheck /><div><strong>{summary.approved}</strong><span>Aprobados</span></div></article>
          <article><Users /><div><strong>{summary.contributors}</strong><span>Colaboradores autorizados</span></div></article>
        </section>
      )}

      <section className="admin-grid" aria-label="Revisión de THERA Field Survey">
        <article className="admin-card">
          <div className="admin-card-title">
            <Route />
            <div>
              <h2>Field Survey pendientes</h2>
              <p>Revisa evidencia GPS antes de alimentar Terrain Engine.</p>
            </div>
          </div>

          {message && <p className="empty-state">{message}</p>}

          {loading ? (
            <p className="empty-state">Cargando sesiones Field Survey...</p>
          ) : pending.length === 0 ? (
            <p className="empty-state">No hay sesiones pendientes.</p>
          ) : (
            pending.map((session) => {
              const points = numberValue(session.activity?.points_count);
              const distance = numberValue(session.activity?.distance_m);
              const reviewable = points >= 2 && distance > 0;
              const busy = busyId === session.id;

              return (
                <div className="route-review-row" key={session.id}>
                  <span className="square-icon"><Route /></span>
                  <div>
                    <strong>{session.source_kind || "Field Survey"}</strong>
                    <small>
                      {formatDistance(session.activity?.distance_m)} ·{" "}
                      {formatDuration(session.activity?.duration_seconds)} ·{" "}
                      {points} puntos GPS ·{" "}
                      {session.activity?.source_platform ?? "plataforma desconocida"}
                    </small>
                    <small>
                      {reviewable
                        ? "Evidencia mínima GPS: OK"
                        : "Evidencia insuficiente: no aprobar"}
                    </small>
                  </div>
                  <div className="route-review-actions">
                    <button
                      type="button"
                      disabled={busy || !reviewable}
                      onClick={() => void reviewSession(session, "approved")}
                    >
                      {busy ? "..." : "Aprobar"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void reviewSession(session, "rejected")}
                    >
                      {busy ? "..." : "Rechazar"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-title">
            <Database />
            <div>
              <h2>Aprobados listos para validar</h2>
              <p>Convierte una sesión aprobada en aporte privado para Terrain Engine.</p>
            </div>
          </div>

          {loading ? (
            <p className="empty-state">Cargando aprobados...</p>
          ) : approvedReady.length === 0 ? (
            <p className="empty-state">
              No hay sesiones aprobadas pendientes de convertir.
            </p>
          ) : (
            approvedReady.map((session) => {
              const points = numberValue(session.activity?.points_count);
              const busy = busyId === session.id;

              return (
                <div className="route-review-row" key={session.id}>
                  <span className="square-icon"><Database /></span>
                  <div>
                    <strong>Track aprobado</strong>
                    <small>
                      {formatDistance(session.activity?.distance_m)} ·{" "}
                      {formatDuration(session.activity?.duration_seconds)} ·{" "}
                      {points} puntos GPS
                    </small>
                    <label>
                      <small>Región destino</small>
                      <select
                        value={regionBySession[session.id] ?? "sierra-sur"}
                        disabled={busy}
                        onChange={(event) =>
                          setRegionBySession((current) => ({
                            ...current,
                            [session.id]: event.target.value,
                          }))
                        }
                      >
                        {REGION_OPTIONS.map(([value, label]) => (
                          <option value={value} key={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <small>
                      Capa: field · Tipo: recorded_track · no modifica mapas base.
                    </small>
                  </div>
                  <div className="route-review-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void createValidatedContribution(session)}
                    >
                      {busy ? "..." : "Crear aporte"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-title">
            <ShieldCheck />
            <div>
              <h2>Terrain Engine · baseline field</h2>
              <p>Build y candidate privados. No publica ni reemplaza PMTiles.</p>
            </div>
          </div>

          {loading ? (
            <p className="empty-state">Cargando Terrain Engine...</p>
          ) : engineSources.length === 0 ? (
            <p className="empty-state">No hay aportes field validados listos.</p>
          ) : (
            engineSources.map((source) => {
              const points = numberValue(source.session.activity?.points_count);
              const buildBusy = busyId === `build:${source.contributionId}`;
              const candidateBusy = busyId === `candidate:${source.contributionId}`;
              const reviewBusy = source.candidateId
                ? busyId === `review:${source.candidateId}`
                : false;
              const releaseBusy = source.candidateId
                ? busyId === `release:${source.candidateId}`
                : false;
              const activationBusy = source.releaseId
                ? busyId === `activate:${source.releaseId}`
                : false;

              return (
                <div className="route-review-row" key={source.contributionId}>
                  <span className="square-icon"><ShieldCheck /></span>
                  <div>
                    <strong>{source.regionId} · field</strong>
                    <small>
                      1 visita · {points} puntos fuente · algoritmo: {ENGINE_ALGORITHM_VERSION}
                    </small>
                    <small>
                      Aporte: {source.contributionId.slice(0, 8)}… · build: {source.buildId ? "creado" : "pendiente"} · candidate: {source.candidateId ? "creado" : "pendiente"}
                    </small>
                    <small>
                      Baseline pass-through: conserva la geometría validada; accuracy/deviation quedan null hasta disponer de evidencia suficiente.
                    </small>
                  </div>
                  <div className="route-review-actions">
                    {!source.buildId ? (
                      <button
                        type="button"
                        disabled={buildBusy || candidateBusy}
                        onClick={() => void createTerrainBuild(source)}
                      >
                        {buildBusy ? "..." : "Crear build"}
                      </button>
                    ) : !source.candidateId ? (
                      <button
                        type="button"
                        disabled={buildBusy || candidateBusy}
                        onClick={() => void createTerrainCandidate(source)}
                      >
                        {candidateBusy ? "..." : "Crear candidate"}
                      </button>
                    ) : source.candidateDecision === null ? (
                      <>
                        <button
                          type="button"
                          disabled={buildBusy || candidateBusy || reviewBusy}
                          onClick={() => void reviewTerrainCandidate(source, "approved")}
                        >
                          {reviewBusy ? "..." : "Aprobar candidate"}
                        </button>
                        <button
                          type="button"
                          disabled={buildBusy || candidateBusy || reviewBusy}
                          onClick={() => void reviewTerrainCandidate(source, "rejected")}
                        >
                          {reviewBusy ? "..." : "Rechazar candidate"}
                        </button>
                      </>
                    ) : source.candidateDecision === "approved" ? (
                      source.releaseId ? (
                        source.releaseActive ? (
                          <span>Release V1 · ACTIVO</span>
                        ) : source.releaseId === PILOT_RELEASE_ID ? (
                          <>
                            <span>Release staged · NO activo</span>
                            <button
                              type="button"
                              disabled={activationBusy}
                              onClick={() => void activateDerivedLayerRelease(source)}
                            >
                              {activationBusy ? "..." : "Activar release V1"}
                            </button>
                          </>
                        ) : (
                          <span>Release staged · activación no configurada</span>
                        )
                      ) : source.candidateId === PILOT_RELEASE_CANDIDATE_ID ? (
                        <button
                          type="button"
                          disabled={releaseBusy}
                          onClick={() => void stageDerivedLayerRelease(source)}
                        >
                          {releaseBusy ? "..." : "Crear release staged"}
                        </button>
                      ) : (
                        <span>Candidate aprobado · release no configurado</span>
                      )
                    ) : (
                      <span>Candidate rechazado</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-title">
            <Database />
            <div>
              <h2>Derived Layer · detección PHASE3H</h2>
              <p>Resuelve el release activo compartido. PHASE3J permite rendering solo en Preview y solo para admin.</p>
            </div>
          </div>

          {derivedDetectionStatus === "disabled" ? (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>Detector desactivado por feature flag</strong>
                <small>VITE_THERA_DERIVED_LAYERS debe estar en true para consultar el resolver.</small>
                <small>Mapa base intacto · Rendering: OFF.</small>
              </div>
            </div>
          ) : derivedDetectionStatus === "loading" ? (
            <p className="empty-state">Detectando release activo sierra-sur · field...</p>
          ) : derivedDetectionStatus === "error" ? (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>No se pudo resolver el release activo</strong>
                <small>{derivedDetectionError ?? "Error de detección."}</small>
                <small>Rendering continúa OFF y el mapa base no cambia.</small>
              </div>
              <div className="route-review-actions">
                <button type="button" onClick={() => void detectActiveDerivedRelease()}>
                  Reintentar
                </button>
              </div>
            </div>
          ) : derivedDetectionStatus === "none" || !activeDerivedRelease ? (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>No hay release activo para sierra-sur · field</strong>
                <small>La app continúa usando únicamente los mapas actuales.</small>
                <small>Rendering: OFF.</small>
              </div>
              <div className="route-review-actions">
                <button type="button" onClick={() => void detectActiveDerivedRelease()}>
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <div className="route-review-row">
              <span className="square-icon"><ShieldCheck /></span>
              <div>
                <strong>Release activo detectado · V{activeDerivedRelease.version}</strong>
                <small>{activeDerivedRelease.region_id} · {activeDerivedRelease.layer}</small>
                <small>Archivo: {activeDerivedRelease.file_path}</small>
                <small>
                  {activeDerivedRelease.size_bytes} bytes · SHA-256: {activeDerivedRelease.sha256.slice(0, 16)}…
                </small>
                <small>
                  Feature flag PHASE3J: {THERA_DERIVED_LAYER_RENDERING_ENABLED ? "ON" : "OFF"} · el overlay se controla desde Explorar → Capas.
                </small>
              </div>
              <div className="route-review-actions">
                <span>DETECTED</span>
                <button type="button" onClick={() => void detectActiveDerivedRelease()}>
                  Verificar otra vez
                </button>
              </div>
            </div>
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-title">
            <ShieldCheck />
            <div>
              <h2>Derived Layer · R2 PHASE3I</h2>
              <p>Comprueba el PMTiles activo detrás del gateway privado antes de habilitar el overlay.</p>
            </div>
          </div>

          {!THERA_DERIVED_LAYERS_ENABLED ? (
            <p className="empty-state">Feature flag desactivado. No se consulta R2.</p>
          ) : !activeDerivedRelease ? (
            <p className="empty-state">Esperando un release activo detectado por PHASE3H.</p>
          ) : derivedR2ProbeStatus === "loading" ? (
            <p className="empty-state">Verificando acceso privado al PMTiles en R2...</p>
          ) : derivedR2ProbeStatus === "error" ? (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>R2 no verificado</strong>
                <small>{derivedR2ProbeError ?? "Error al verificar R2."}</small>
                <small>Overlay no habilitado · el mapa base continúa intacto.</small>
              </div>
              <div className="route-review-actions">
                <button type="button" onClick={() => void probeActiveDerivedReleaseR2()}>
                  Reintentar R2
                </button>
              </div>
            </div>
          ) : derivedR2ProbeStatus === "reachable" && derivedR2Probe ? (
            <div className="route-review-row">
              <span className="square-icon"><ShieldCheck /></span>
              <div>
                <strong>R2 REACHABLE · SIZE OK</strong>
                <small>{derivedR2Probe.alias} · HTTP {derivedR2Probe.status}</small>
                <small>Content-Length: {derivedR2Probe.content_length} bytes · esperado: {activeDerivedRelease.size_bytes}</small>
                <small>Content-Type: {derivedR2Probe.content_type ?? "sin metadata"} · listo para control PHASE3J.</small>
              </div>
              <div className="route-review-actions">
                <span>CONNECTED</span>
                <button type="button" onClick={() => void probeActiveDerivedReleaseR2()}>
                  Verificar otra vez
                </button>
              </div>
            </div>
          ) : derivedR2ProbeStatus === "mismatch" && derivedR2Probe ? (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>R2 REACHABLE · SIZE MISMATCH</strong>
                <small>Recibido: {derivedR2Probe.content_length ?? "desconocido"} bytes · esperado: {activeDerivedRelease.size_bytes}</small>
                <small>No habilitar rendering hasta resolver la diferencia.</small>
              </div>
              <div className="route-review-actions"><span>BLOCKED</span></div>
            </div>
          ) : (
            <div className="route-review-row">
              <span className="square-icon"><Database /></span>
              <div>
                <strong>Release V{activeDerivedRelease.version} detectado · R2 pendiente</strong>
                <small>{activeDerivedRelease.region_id} · {activeDerivedRelease.layer}</small>
                <small>Solo hará HEAD autenticado; no descarga ni renderiza el PMTiles.</small>
              </div>
              <div className="route-review-actions">
                <button type="button" onClick={() => void probeActiveDerivedReleaseR2()}>
                  Verificar R2
                </button>
              </div>
            </div>
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-title">
            <ShieldCheck />
            <div>
              <h2>Derived Layer · overlay PHASE3J</h2>
              <p>Prueba visual controlada. No reemplaza mapas base ni está habilitada en producción o Android.</p>
            </div>
          </div>

          <div className="route-review-row">
            <span className="square-icon"><ShieldCheck /></span>
            <div>
              <strong>{THERA_DERIVED_LAYER_RENDERING_ENABLED ? "Overlay preparado para Preview" : "Overlay desactivado"}</strong>
              <small>Host permitido: phase3j-overlay.thera-rutas-ecuador.pages.dev</small>
              <small>Acceso: administrador aprobado · fuente: sierra-sur · field · Release activo.</small>
              <small>Actívalo manualmente en Explorar → Capas → Field Survey V1.</small>
            </div>
            <div className="route-review-actions"><span>{THERA_DERIVED_LAYER_RENDERING_ENABLED ? "READY" : "OFF"}</span></div>
          </div>
        </article>
      </section>
    </>
  );
}
